import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError, setupProfile } from "@/api/client";
import { PageTitle } from "@/components/PageTitle";
import { HANDLE_REGEX, type CardVisibility } from "@/api/types";
import { SignedInHeader } from "@/auth/SignedInHeader";
import { ComicPanel } from "@/components/comic";
import { Switch } from "@/components/ui/switch";
import { useHtmlLang } from "@/lib/useHtmlLang";

import { useProfile } from "./ProfileContext";
import { ProfileProgress, type ProgressStep } from "./ProfileProgress";
import { browserTimeZone, TimezoneField } from "./TimezoneField";
import { useFeature } from "./useFeature";

/**
 * First-time-setup page. Mounted at {@code /profile/setup}; users land
 * here automatically from {@link RequireHandle} when their handle is
 * null. Once they pick a handle and POST succeeds, the profile context
 * updates and we redirect back to either the page they were trying to
 * reach or to {@code /}.
 *
 * <p>Form fields:</p>
 * <ul>
 *   <li><b>handle</b> — required, immutable in v1. Client validates
 *       against {@code HANDLE_REGEX} so the user gets format feedback
 *       before paying for a round trip. Server is still authoritative —
 *       a 409 means handle is taken, 400 means it's reserved.</li>
 *   <li><b>first name / last name</b> — pre-filled from the existing
 *       profile (Cognito-derived) but editable so users can correct what
 *       sign-up captured.</li>
 * </ul>
 *
 * <p>v1 simplification: no live availability check (no debounced GET
 * /contacts/search?q=...). Users hit submit and see the 409 inline if
 * the handle is taken. Phase 3 will add the privacy-aware lookup that
 * could power live suggestions — but for now we keep it simple.</p>
 */
export function ProfileSetupPage() {
  // Interior copy is hardcoded English — declare it (see AppShell).
  useHtmlLang("en");
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setProfile } = useProfile();

  // Pre-fill names from the existing profile (Cognito-captured `given_name`
  // / `family_name` claims).
  const initialFirst = state.status === "ready" ? state.profile.firstName ?? "" : "";
  const initialLast = state.status === "ready" ? state.profile.lastName ?? "" : "";

  // First/last name are mandatory at sign-up, so a normal new account already
  // carries both — show them READ-ONLY here rather than inviting an edit of what
  // was just verified. Accounts that predate mandatory names, or arrive without
  // them via a future federated IdP, have one or both blank: fall back to
  // editable + required fields so they can still be captured. Derived (not state)
  // so it settles correctly once the profile GET resolves.
  const profileFirst = (state.status === "ready" ? state.profile.firstName ?? "" : "").trim();
  const profileLast = (state.status === "ready" ? state.profile.lastName ?? "" : "").trim();
  const namesLocked = profileFirst !== "" && profileLast !== "";

  const [handle, setHandle] = React.useState("");
  const [firstName, setFirstName] = React.useState(initialFirst);
  const [lastName, setLastName] = React.useState(initialLast);
  const [timezone, setTimezone] = React.useState(browserTimeZone());
  // Onboarding guides toward being discoverable (so others can find + connect);
  // one tap opts out to private.
  const [discoverable, setDiscoverable] = React.useState(true);
  // The question is only about the agent card, and the card is AI_SCHEDULING (Pro and above). Asked
  // of a Free or Basic user it is a choice with no consequence: they have no card to show or hide,
  // cannot be reached by an agent, and their branded booking links resolve either way. So it is
  // simply not asked, and the discoverable default above is what gets stored — which is also the
  // right state to inherit on the day they upgrade. Pro accounts still choose here.
  const cardFeature = useFeature("AI_SCHEDULING");
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);


  // Sync defaults when the profile arrives (we render before profile-ready).
  React.useEffect(() => {
    if (state.status !== "ready") return;
    setFirstName((cur) => (cur === "" ? state.profile.firstName ?? "" : cur));
    setLastName((cur) => (cur === "" ? state.profile.lastName ?? "" : cur));
  }, [state]);

  // If profile is already set, don't let users linger here — bounce them.
  // (Loose != so an omitted field counts as "no handle", mirroring the
  // RequireHandle gate.)
  if (state.status === "ready" && state.profile.handle != null) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  // Strip a leading "@" if the user pasted "@chris" and lowercase as they
  // type so the inline validation hint matches what we'll send.
  const normalisedHandle = handle.trim().replace(/^@/, "").toLowerCase();
  const handleFormatOk = HANDLE_REGEX.test(normalisedHandle);
  const handleHint = describeHandleHint(handle, normalisedHandle, handleFormatOk);
  const namesOk = firstName.trim() !== "" && lastName.trim() !== "";
  const canSubmit = handleFormatOk && namesOk && !submitting;

  // The one goal gradient — the path from "just signed up" to a working account.
  // Handle + name is the step you're on now; an availability pattern is already seeded; the
  // rest happen at your own pace once you're in (booking is the natural first move).
  const journeySteps: ProgressStep[] = [
    { label: "Claim your handle + name", done: handleFormatOk && namesOk },
    { label: "Have an availability pattern (ready for you)", done: true },
    { label: "Make a booking", done: false },
    { label: "Connect a calendar", done: false },
    { label: "Connect with someone → unlock the chat", done: false },
  ];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!handleFormatOk) {
      setServerError("Handle doesn't match the format above.");
      return;
    }
    if (!namesOk) {
      setServerError("First and last name are required.");
      return;
    }
    setServerError(null);
    setSubmitting(true);
    try {
      const updated = await setupProfile({
        handle: normalisedHandle,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        timezone,
        // Only sent when the question was asked, which is Pro only. Everyone else is stored
        // private by the server: with no card and no contacts search, "discoverable" would be a
        // state they carry without a control for it.
        cardVisibility: cardFeature === "granted"
          ? ((discoverable ? "discoverable" : "private") as CardVisibility)
          : undefined,
      });
      // Push the new profile into context optimistically so RequireHandle
      // admits us on the next navigation without a second GET.
      setProfile(updated);
      // Land new users on their booking page — the useful thing to do on day one
      // (the chat needs a connection first). Making a booking is also how they get
      // their first connection.
      // Land on Home: the same overview every tier gets, with the goal tracker
      // showing what to do next — rather than dropping them into one feature.
      navigate("/?welcome=1", { replace: true });
    } catch (err) {
      setServerError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    /* translate="no" — see AppShell: this onboarding gate sits outside the
       shell, so it needs its own opt-out. */
    <div className="mx-auto max-w-2xl space-y-4 p-6 font-comic-body" translate="no">
      <header className="flex items-start justify-between gap-3">
        <div>
          <PageTitle>Set up your account</PageTitle>
          <p className="text-xs text-muted-foreground">
            A handle and your name are all it takes to get in. Your handle is how others tag you,
            like <em>"find 30 minutes with @{normalisedHandle || "you"}"</em>. It's permanent, so
            pick one you like.
          </p>
        </div>
        <SignedInHeader />
      </header>

      <ProfileProgress steps={journeySteps} />

      <ComicPanel title="One-time setup">
        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-2">
            <label
              htmlFor="handle"
              className="block font-comic-headline text-sm uppercase tracking-wide"
            >
              Handle
            </label>
            <div className="flex items-stretch gap-0 overflow-hidden rounded-md border-2 border-border">
              <span className="flex items-center bg-muted px-3 font-comic-headline text-base text-muted-foreground">
                @
              </span>
              <input
                id="handle"
                name="handle"
                type="text"
                inputMode="text"
                autoCapitalize="off"
                autoComplete="off"
                spellCheck={false}
                placeholder="chris"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                disabled={submitting}
                className="flex-1 bg-background px-3 py-2 text-sm focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <p
              className={
                handleHint.tone === "error"
                  ? "text-xs text-destructive-strong"
                  : handleHint.tone === "ok"
                  ? "text-xs text-brand-blue"
                  : "text-xs text-muted-foreground"
              }
            >
              {handleHint.text}
            </p>
          </div>

          {namesLocked ? (
            // Read-only: captured (and required) at sign-up — displayed, not re-asked.
            <div className="space-y-2">
              <span className="block font-comic-headline text-sm uppercase tracking-wide">
                Your name
              </span>
              <div className="flex items-center justify-between gap-3 rounded-md border-2 border-border bg-muted px-3 py-2">
                <span className="truncate text-sm">
                  {profileFirst} {profileLast}
                </span>
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
                  From sign-up
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Captured when you created your account.
              </p>
            </div>
          ) : (
            // Fallback for accounts missing a name (pre-mandatory, or federated).
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label
                  htmlFor="firstName"
                  className="block font-comic-headline text-sm uppercase tracking-wide"
                >
                  First name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border-2 border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="lastName"
                  className="block font-comic-headline text-sm uppercase tracking-wide"
                >
                  Last name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border-2 border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          )}

          <TimezoneField value={timezone} onChange={setTimezone} disabled={submitting} />

          {cardFeature === "granted" && (
          <div className="rounded-md border-2 border-border bg-muted p-3">
            <label className="flex items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block font-comic-headline text-sm uppercase tracking-wide">
                  Be discoverable
                </span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-muted-foreground">
                  Let other members find you by @{normalisedHandle || "handle"} and reach your agent.
                  Switch to private anytime.
                </span>
              </span>
              <Switch checked={discoverable} onCheckedChange={setDiscoverable} disabled={submitting} />
            </label>
            {!discoverable && (
              <p className="mt-2 rounded-md border-2 border-warning/40 bg-warning/10 p-2 text-[11px] leading-relaxed text-warning-strong">
                While private, only you can see your agent card, and other members cannot find you
                by handle. Your booking links are unaffected.
              </p>
            )}
          </div>
          )}

          {serverError && (
            <div
              role="alert"
              className="rounded-md border-2 border-border bg-destructive/10 px-3 py-2 text-sm text-destructive-strong"
            >
              {serverError}
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-muted-foreground">
              Allowed: lowercase letters and digits only. 3–30 characters.
              This becomes your agent&apos;s public address (/@handle).
            </p>
            <button
              type="submit"
              disabled={!canSubmit}
              className="comic-shadow-sm flex h-[44px] items-center gap-2 rounded-md border-2 border-border bg-brand-blue text-brand-blue-foreground px-4 font-comic-headline uppercase tracking-wide hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)]"
            >
              {submitting ? "Saving…" : "Set up my account"}
            </button>
          </div>
        </form>
      </ComicPanel>
    </div>
  );
}

/**
 * Build the inline hint under the handle field. Three tones map to three
 * colour states: a neutral hint while the field is empty, a red error
 * while the typed value doesn't match the format, and a green confirmation
 * when it does. The hint stays compact — the full rule is also shown next
 * to the submit button.
 */
function describeHandleHint(
  raw: string,
  normalised: string,
  formatOk: boolean,
): { tone: "neutral" | "error" | "ok"; text: string } {
  if (raw.trim() === "") {
    return {
      tone: "neutral",
      text: "Pick a unique handle. You'll be findable by @handle in the app.",
    };
  }
  if (!formatOk) {
    return {
      tone: "error",
      text: `"@${normalised || raw.trim()}" isn't a valid handle. See the rules below.`,
    };
  }
  return {
    tone: "ok",
    text: `Looks good. Other agents will see you as @${normalised}.`,
  };
}

/**
 * Translate API errors into form-friendly copy. 409 means handle is taken
 * (or already set, in which case the gate would have bounced us — but
 * defensive); 400 means format/reserved-word rejection.
 */
function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return "That handle is taken. Try another.";
    }
    if (err.status === 400) {
      // Server returns a plain-text body via ResponseStatusException with a
      // useful reason. Surface it directly if present, else a generic message.
      const reason = extractReason(err.body);
      return reason ?? "That handle isn't allowed. Try another.";
    }
    return `Couldn't save: ${err.status} ${err.message}`;
  }
  return err instanceof Error ? err.message : "Couldn't save your handle.";
}

/**
 * Spring's {@code ResponseStatusException} surfaces its reason inside a
 * JSON body shaped roughly like {@code {"error":"Bad Request","message":"handle is reserved..."}}.
 * Best-effort: parse the JSON and pull out a useful string; if it's not
 * JSON, fall back to the raw body trimmed.
 */
function extractReason(body: string): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as { message?: string; error?: string };
    return parsed.message ?? parsed.error ?? null;
  } catch {
    return body.length < 200 ? body : null;
  }
}
